/**
 * Admin Portal — Data Lifecycle & Retention Policy Management page.
 * Configure retention policies, view upcoming deletions, and audit log.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, TextField,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { Timer, Edit } from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

interface RetentionPolicyItem {
  id: string;
  data_type: string;
  retention_days: number;
  is_configurable: boolean;
  description: string | null;
  updated_at: string | null;
}

interface UpcomingDeletion {
  id: string;
  data_type: string;
  organization_id: string | null;
  file_name: string | null;
  expires_at: string | null;
  days_remaining: number;
}

interface AuditLogEntry {
  id: string;
  data_type: string;
  record_id: string;
  organization_id: string | null;
  deletion_reason: string;
  deleted_by: string;
  metadata: any;
  deleted_at: string | null;
}

const DataLifecycle: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [policies, setPolicies] = useState<RetentionPolicyItem[]>([]);
  const [upcoming, setUpcoming] = useState<UpcomingDeletion[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<RetentionPolicyItem | null>(null);
  const [editDays, setEditDays] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    const fetchFn = tab === 0
      ? authFetch('/api/admin/portal/retention-policies')
      : tab === 1
      ? authFetch('/api/admin/portal/retention/upcoming')
      : authFetch('/api/admin/portal/audit-log');

    fetchFn.then((r) => r.json())
      .then((data) => {
        if (data.success) {
          if (tab === 0) setPolicies(data.policies);
          else if (tab === 1) setUpcoming(data.upcoming_deletions);
          else setAuditLog(data.audit_log);
        }
      })
      .catch(() => setError('Failed to load data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, [tab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdatePolicy = async () => {
    if (!editDialog) return;
    try {
      await authFetch(`/api/admin/portal/retention-policies/${editDialog.id}`, {
        method: 'PUT',
        body: JSON.stringify({ retention_days: parseInt(editDays) }),
      });
      setEditDialog(null);
      fetchData();
    } catch {
      setError('Failed to update policy');
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Data Lifecycle
      </Typography>
        <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Retention Policies" />
        <Tab label="Upcoming Deletions" />
        <Tab label="Audit Log" />
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {/* Retention Policies Tab */}
          {tab === 0 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Data Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Retention (days)</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Configurable</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {policies.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {p.data_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </TableCell>
                      <TableCell>
                        <Chip label={`${p.retention_days} days`} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={p.is_configurable ? 'Yes' : 'Locked'}
                          size="small"
                          color={p.is_configurable ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>{p.description || '—'}</TableCell>
                      <TableCell>
                        {p.is_configurable && (
                          <Button
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => { setEditDialog(p); setEditDays(String(p.retention_days)); }}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Upcoming Deletions Tab */}
          {tab === 1 && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Items below will be automatically deleted when their retention period expires.
              </Alert>
              {upcoming.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No items approaching deletion.</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>File/Record</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Expires</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Days Left</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {upcoming.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.data_type}</TableCell>
                          <TableCell>{item.file_name || item.id.slice(0, 8)}</TableCell>
                          <TableCell>{item.organization_id?.slice(0, 8) || '—'}</TableCell>
                          <TableCell>
                            {item.expires_at ? new Date(item.expires_at).toLocaleDateString() : '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              icon={<Timer />}
                              label={`${item.days_remaining} days`}
                              size="small"
                              color={item.days_remaining <= 3 ? 'error' : 'warning'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Audit Log Tab */}
          {tab === 2 && (
            <>
              {auditLog.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No deletion events recorded.</Typography>
                </Paper>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Deleted At</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Data Type</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Deleted By</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLog.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell>
                            {log.deleted_at ? new Date(log.deleted_at).toLocaleString() : '—'}
                          </TableCell>
                          <TableCell>{log.data_type}</TableCell>
                          <TableCell>
                            <Chip label={log.deletion_reason} size="small" />
                          </TableCell>
                          <TableCell>{log.deleted_by}</TableCell>
                          <TableCell>{log.organization_id?.slice(0, 8) || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}

      {/* Edit Policy Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Retention Policy</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {editDialog?.data_type.replace(/_/g, ' ')} — {editDialog?.description}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Retention Days"
            value={editDays}
            onChange={(e) => setEditDays(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdatePolicy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataLifecycle;
